import * as React from "react";
import { styled } from "styled-components";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";
import { css } from "styled-components";
import type { BackgroundColor, MobileColumns } from "../../types/theme.js";
import {
  Card as BaseCard,
  CardBody,
} from "../../internal-components/Card.js";

export interface Employee {
  image?: Image;
  name?: string;
  title?: string;
  text?: string;
}

export interface Props {
  text?: string;
  columns?: 3 | 4;
  mobileColumns?: MobileColumns;
  backgroundColor?: BackgroundColor;
  employees?: Employee[];
}

export const Employees: React.FunctionComponent<Props> = ({
  text,
  columns = 3,
  mobileColumns = 1,
  backgroundColor,
  employees = [],
}) => {
  if (!text && employees.length === 0) {
    return null;
  }

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer>
          {text && (
            <ComponentTitle>
              <RichText
                dangerouslySetInnerHTML={{ __html: text }}
                {...createEditableProps("html", "text")}
              />
            </ComponentTitle>
          )}
          <Grid $columns={columns} $mobileColumns={mobileColumns}>
            {employees.map((e, k) => (
              <Item key={k} $bg={backgroundColor}>
                {e.image && (
                  <img
                    src={transformImageUrl(e.image, {
                      width: 588,
                      height: 634,
                    })}
                    alt={e.image.altText}
                    loading="lazy"
                  />
                )}
                <ItemContent>
                  {e.name && (
                    <h3 {...createEditableProps("text", `employees.${k}.name`)}>
                      {e.name}
                    </h3>
                  )}
                  {e.title && (
                    <p
                      className="title"
                      {...createEditableProps("text", `employees.${k}.title`)}
                    >
                      {e.title}
                    </p>
                  )}
                  {e.text && (
                    <RichText
                      dangerouslySetInnerHTML={{ __html: e.text }}
                      {...createEditableProps("html", `employees.${k}.text`)}
                    />
                  )}
                </ItemContent>
              </Item>
            ))}
          </Grid>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

export const ComponentTitle = styled.div`
  h1, h2, h3, h4, h5, h6 {
    color: var(--employees-component-title-color);
  }
`;

export const Grid = styled.div<{
  $columns: number;
  $mobileColumns: MobileColumns;
}>`
  display: grid;
  grid-gap: ${(props) => props.theme.spacing.s};
  grid-auto-rows: auto;
  margin-top: ${(props) => props.theme.spacing.s};
  grid-template-columns: repeat(${(props) => props.$mobileColumns}, minmax(0, 1fr));

  @media (min-width: 960px) {
    grid-template-columns: repeat(${(props) => props.$columns || 3}, minmax(0, 1fr));
    margin-top: ${(props) => props.theme.spacing.m};
  }
`;

export const Item = styled(BaseCard).attrs({
  as: "div",
  $background: "var(--employees-card-background)",
})`
  ${(p) =>
    !p.$bg &&
    css`
      h3 {
        color: var(--employees-card-name-color, inherit);
      }
      .title {
        color: var(--employees-card-role-color, inherit);
      }
      .rich-text {
        color: var(--employees-card-description-color, inherit);
      }
    `}

  * {
    margin: 0;
  }

  img {
    display: block;
    width: 100%;
    aspect-ratio: 260 / 280;
    object-fit: cover;
    border-top-left-radius: ${(props) => props.theme.borderRadius};
    border-top-right-radius: ${(props) => props.theme.borderRadius};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .title {
    font-weight: 600;
  }

  .rich-text {
    a {
      color: inherit;
      text-decoration: none;
    }
  }
`;

export const ItemContent = styled(CardBody)`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.xs};

  h3, p {
    margin: 0;
  }
`;
